import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";

import { RouletteContext } from "./RouletteContext";
import { clientAp } from "../../constants/general";
import { SUBSCRIPTION_ROULETTE } from "../../constants/roulette";

import {
    addRouletteBetting,
    clearCartRoulette,
    deleteRouletteBetting,
    fetchRouletteDrawingResult,
    fetchRouletteListBetting,
    fetchRouletteRecentBetReport,
    fetchRouletteRecentDraw,
    fetchStatisticRoulette,
    rouletteConfirmBettingNow,
} from "./action";

import {
    fetchUserBalance,
    fetchUserBalanceValidate,
    setIsAddToCart,
    setIsAutoPrint,
} from "../user/action";

import {
    playCountDownAudio,
    playGoodLuckAudio,
} from "../audio/action";

import { useRouletteSpin } from "./hooks/useRouletteSpin";

function RouletteProvider(props) {
    const {
        children,
        t,
        draw,
        oldResult,

        fetchRouletteDrawingResult,
        fetchStatisticRoulette,
        fetchRouletteRecentDraw,
        fetchUserBalance,
        fetchRouletteRecentBetReport,
        clearCartRoulette,

        playCountDownAudio,
        playGoodLuckAudio,
    } = props;

    const [status, setStatus] = useState(undefined);
    const [timer, setTimer] = useState(0);
    const [isStopBet, setIsStopBet] = useState(false);
    const [pendingSpin, setPendingSpin] = useState(false);

    const subRef = useRef(null);
    const previousStatusRef = useRef(null);
    const previousDrawCodeRef = useRef(null);
    const spinLockRef = useRef(false);
    const spinUnlockTimeoutRef = useRef(null);
    const startDelayTimeoutRef = useRef(null);

    const {
        isWin,
        setIsWin,
        speed,
        doc,
        position,
        start,
        endroll,
        spin,
        setIntSpin,
        restartAnimation,
        clearSpinTimers,
    } = useRouletteSpin({ oldResult });

    const clearAllTimers = useCallback(() => {
        clearSpinTimers();

        if (spinUnlockTimeoutRef.current) {
            clearTimeout(spinUnlockTimeoutRef.current);
            spinUnlockTimeoutRef.current = null;
        }

        if (startDelayTimeoutRef.current) {
            clearTimeout(startDelayTimeoutRef.current);
            startDelayTimeoutRef.current = null;
        }
    }, [clearSpinTimers]);

    const verifyCurrentDraw = useCallback(
        (currentDrawCode) => {
            try {
                const betList = JSON.parse(
                    sessionStorage.getItem("betting_roulette") || "[]"
                );

                if (!betList.length) return;

                const firstItem = betList[0];

                if (
                    firstItem?.drawCode !== currentDrawCode &&
                    typeof clearCartRoulette === "function"
                ) {
                    clearCartRoulette();
                }
            } catch (error) {
            }
        },
        [clearCartRoulette]
    );

    const loadDrawing = useCallback(async () => {
        setIsWin(false);

        if (typeof fetchRouletteDrawingResult !== "function") {
            return null;
        }

        try {
            await fetchRouletteDrawingResult();
            return true;
        } catch (error) {
            return null;
        }
    }, [fetchRouletteDrawingResult, setIsWin]);

    const unlockSpinLater = useCallback(() => {
        if (spinUnlockTimeoutRef.current) {
            clearTimeout(spinUnlockTimeoutRef.current);
        }

        spinUnlockTimeoutRef.current = setTimeout(() => {
            spinLockRef.current = false;
            spinUnlockTimeoutRef.current = null;
        }, 10000);
    }, []);

    const requestSpin = useCallback(async () => {
        if (spinLockRef.current) {
            return;
        }

        spinLockRef.current = true;
        setPendingSpin(true);

        await loadDrawing();
    }, [loadDrawing]);

    const refreshData = useCallback(() => {
        loadDrawing();

        if (typeof fetchStatisticRoulette === "function") {
            fetchStatisticRoulette();
        }

        if (typeof fetchRouletteRecentDraw === "function") {
            fetchRouletteRecentDraw();
        }

        if (typeof fetchUserBalance === "function") {
            fetchUserBalance();
        }

        if (typeof fetchRouletteRecentBetReport === "function") {
            fetchRouletteRecentBetReport();
        }

        setIsStopBet(false);
    }, [
        loadDrawing,
        fetchStatisticRoulette,
        fetchRouletteRecentDraw,
        fetchUserBalance,
        fetchRouletteRecentBetReport,
    ]);

    const onDrawStatusChange = useCallback(
        (currentDraw) => {
            if (!currentDraw) return;

            const nextStatus = currentDraw.status;
            const nextDrawCode = currentDraw?.drawCode ?? currentDraw?.code ?? null;

            const previousStatus = previousStatusRef.current;
            const previousDrawCode = previousDrawCodeRef.current;

            const statusChanged = previousStatus !== nextStatus;
            const drawChanged = previousDrawCode !== nextDrawCode;

            if (statusChanged) {
                if (nextStatus === "RELEASE") {
                    requestSpin();
                }

                if (nextStatus === "OPEN") {
                    spinLockRef.current = false;
                    setPendingSpin(false);
                    refreshData();
                }
            }

            if (drawChanged && nextDrawCode) {
                verifyCurrentDraw(nextDrawCode);
            }

            previousStatusRef.current = nextStatus;
            previousDrawCodeRef.current = nextDrawCode;
            setStatus(nextStatus);
        },
        [requestSpin, refreshData, verifyCurrentDraw]
    );

    const subscription = useCallback(() => {
        subRef.current = clientAp
            .subscribe({
                query: SUBSCRIPTION_ROULETTE,
            })
            .subscribe({
                next: (event) => {
                    if (event?.data?.v_roulette_drawing) {
                        const currentDraw = event.data.v_roulette_drawing[0] || {};
                        onDrawStatusChange(currentDraw);
                    }
                },
                error: (err) => {
                    console.error("Roulette subscription error:", err);
                },
            });
    }, [onDrawStatusChange]);

    const unsubscription = useCallback(() => {
        if (subRef.current) {
            subRef.current.unsubscribe();
            subRef.current = null;
        }
    }, []);

    const onLastTenSecond = useCallback(
        (time) => {
            setTimer(time);
            setIsStopBet(true);

            if (typeof playCountDownAudio === "function" && time > 0) {
                playCountDownAudio(time - 1);
            }

            if (time === 0) {
                if (typeof playGoodLuckAudio === "function") {
                    playGoodLuckAudio();
                }

                setTimeout(() => {
                    if (!spinLockRef.current) {
                        requestSpin();
                    }
                }, 800);
            }
        },
        [playCountDownAudio, playGoodLuckAudio, requestSpin]
    );

    // Wait until Redux oldResult is actually available, then spin
    useEffect(() => {
        if (!pendingSpin) return;
        if (!oldResult?.result) return;
        if (startDelayTimeoutRef.current) {
            clearTimeout(startDelayTimeoutRef.current);
        }

        startDelayTimeoutRef.current = setTimeout(() => {
            start();
            setPendingSpin(false);
            unlockSpinLater();
            startDelayTimeoutRef.current = null;
        }, 1200);
    }, [pendingSpin, oldResult, start, unlockSpinLater]);

    useEffect(() => {
        refreshData();
        subscription();

        return () => {
            unsubscription();
            clearAllTimers();
        };
    }, [refreshData, subscription, unsubscription, clearAllTimers]);

    const contextValue = useMemo(
        () => ({
            ...props,
            draw,
            oldResult,
            status,
            isWin,
            timer,
            isStopBet,
            start,
            endroll,
            spin,
            setIntSpin,
            restartAnimation,
            onLastTenSecond,
            speed,
            doc,
            position,
            t,
        }),
        [
            props,
            draw,
            oldResult,
            status,
            isWin,
            timer,
            isStopBet,
            start,
            endroll,
            spin,
            setIntSpin,
            restartAnimation,
            onLastTenSecond,
            speed,
            doc,
            position,
            t,
        ]
    );

    return (
        <RouletteContext.Provider value={contextValue}>
            {children}
        </RouletteContext.Provider>
    );
}

const mapStateToProps = (state) => ({
    ...state.roulette.drawing,
    config: state.config,
    betting: state.roulette.betting,
    user: state.user,

    draw: state?.roulette?.drawing?.draw || {},
    oldResult: state?.roulette?.drawing?.oldResult || null,
    lastDraws: state?.roulette?.drawing?.lastDraws || [],
    statistic: state?.roulette?.drawing?.statistic || {
        underOverItems: [],
        numberItems: [],
    },
    recentBetting: state?.roulette?.drawing?.recentBetting || [],
    drawingApi: state?.roulette?.drawing?.api?.drawingResult || {
        loading: false,
        error: null,
        lastSuccessAt: null,
    },
});

const mapDispatchToProps = {
    fetchStatisticRoulette,
    fetchRouletteDrawingResult,
    fetchRouletteRecentDraw,
    fetchUserBalance,
    fetchRouletteRecentBetReport,
    clearCartRoulette,

    addRouletteBetting,
    rouletteConfirmBettingNow,
    fetchUserBalanceValidate,
    setIsAddToCart,
    setIsAutoPrint,
    fetchRouletteListBetting,
    deleteRouletteBetting,
    playCountDownAudio,
    playGoodLuckAudio,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withTranslation()(RouletteProvider));
